/**
 * Game settings — one persisted blob, applied on boot. (§7.1, §6.8, §4.1)
 *
 * ── Shape and philosophy ────────────────────────────────────────────────────
 *
 * Everything a player can tune lives in ONE versioned localStorage key,
 * `fg.settings.v1`: camera rig, graphics quality, the full rebindable control
 * map, practice-match knobs (half length, AI difficulty) and the master-volume
 * stub. One blob means one load, one save, one migration story — and the
 * settings screen edits a plain object it can hand back to `saveSettings`.
 *
 * Pure logic (defaults / parse / serialize / clamp) is separated from storage
 * so it is unit-testable in node with no DOM. `parseSettings` NEVER throws and
 * never returns a partially-valid object: every field is individually
 * validated against the defaults, so a corrupt blob (or one written by a newer
 * client) degrades field-by-field instead of wholesale.
 *
 * ── How each group takes effect ─────────────────────────────────────────────
 *
 *  camera   render/camera.ts reads TUNABLES.camera EVERY FRAME (that is the
 *           §7.1 "live preview" seam the dev overlay already proved out), so
 *           `applyCameraSettings` just writes the object. In a match the
 *           change is visible the same frame; from the menu it simply persists.
 *  quality  Renderer takes a QualityTier override at construction and exposes
 *           setQuality() for live changes — both are wired in settingsScreen.
 *  controls InputManager.setMap() takes a new ControlMap live.
 *  volume   game/audio.ts's createAudioSystem().setMasterVolume() reads this on
 *           boot and live via the settings screen's `audio` LiveHooks entry —
 *           same pattern as camera/quality/input above (§11).
 */

import { TUNABLES } from '@fg/shared/sim';
import type { Difficulty } from '@fg/shared/ai';
import { CameraMode } from '../render/camera.js';
import type { QualityTier } from '../render/context.js';
import {
  defaultControlMap,
  parseControlMap,
  type ControlMap,
} from '../input/controlMap.js';
import { DEFAULT_AIM_SENSITIVITY, DEFAULT_MOUSE_SENSITIVITY } from '../input/InputManager.js';
import { CROSSHAIR_SIZE_CHOICES, DEFAULT_CROSSHAIR_SIZE_INDEX } from './crosshair-geometry.js';

// Bumped v1 → v2 (2026-07-23): the comfort-tuned camera sensitivity default
// (0.0028 → 0.0018) must actually reach existing testers, whose v1 blob would
// otherwise pin the old value. A new key = a clean start on the new defaults.
//
// Bumped v2 → v3 (2026-07-25): THE DEFAULT CAMERA MODE CHANGED (Broadcast →
// TacticalFollow — see defaultSettings for the full reasoning). A persisted v2
// blob pins `camera.mode: 0`, which IS the broken state this change exists to
// fix, and anyone who has played this build already has one. Without the bump
// the fix would reach nobody who matters.
//
// Unlike v1→v2 this is a MIGRATION, not a clean start: throwing away a tester's
// rebound keys, tuned sensitivities and match preferences to correct one number
// is a worse trade than writing eight lines. See `migrateV2`/`loadSettings`.
export const SETTINGS_KEY = 'fg.settings.v3';
/** The v2 key, read once by `loadSettings` when v3 is absent (see `migrateV2`). */
export const SETTINGS_KEY_V2 = 'fg.settings.v2';

/** 'auto' = context.ts's autoDetectQuality picks the tier at renderer boot. */
export type QualityChoice = 'auto' | QualityTier;

/**
 * Match-presentation time of day. Structurally identical to lighting.ts's
 * SkyPreset ON PURPOSE, but declared locally so this module (which the node
 * test-suite imports) never touches a file that imports three.js.
 */
export type TimeOfDay = 'day' | 'golden' | 'overcast' | 'night';

/** 'rain' sets MatchConfig.wetPitch — REAL sim physics (§3.2 wet friction/
 *  restitution) — and the vfx rain/wet sheen reads the same flag off the
 *  config, so one choice drives both honestly. */
export type Weather = 'clear' | 'rain';

export interface CameraSettings {
  /** CameraMode enum value (0..5). */
  mode: number;
  height: number;
  distance: number;
  fov: number;
  /** Radians of CAMERA-look rotation per pixel of mouse motion (§7). Recovered
   *  reference default 0.0028 — see InputManager DEFAULT_MOUSE_SENSITIVITY. */
  mouseSensitivity: number;
  /**
   * Radians of rotation per pixel of mouse motion WHILE A SHOT/PASS IS CHARGING
   * — the "precision aim" gain, the shooter-genre ADS-sensitivity idea.
   *
   * It used to turn a second, invisible accumulator (`InputState.aimYaw`) that
   * the feet arrow displayed. With the arrow gone and the crosshair owning the
   * aim, a separate aim heading could only ever disagree with the reticle, so
   * there is exactly ONE heading now — and this slider is what the mouse turns
   * it by during a charge, while `mouseSensitivity` governs free look. Two
   * knobs, still independent, but they no longer point two different ways.
   * (See InputManager.feedMouseMove.)
   */
  aimSensitivity: number;
  /** Camera position smoothing, seconds (the critically-damped spring time in
   *  render/camera.ts). Lower = snappier, higher = floatier. Default 0.28. */
  smoothing: number;
  /** Invert the vertical look axis. Persisted for the settings contract and for
   *  camera modes with free vertical look; the shipped broadcast/orbit follow
   *  cams derive pitch from rig geometry, so it is inert for them today. */
  invertY: boolean;
  /** §10 Ball Focus bias on the tactical cam: 0 off / 1 soft / 2 full. */
  ballFocus: number;
}

export interface GameSettings {
  version: 3;
  /** Guest display name (§9.4 — the only identity a guest has). */
  playerName: string;
  camera: CameraSettings;
  quality: QualityChoice;
  controls: ControlMap;
  /** Practice-match half length, minutes. */
  halfMinutes: number;
  /** Practice-match AI difficulty. */
  difficulty: Difficulty;
  /** Match-setup defaults, remembered between launches. */
  timeOfDay: TimeOfDay;
  weather: Weather;
  /** 0..1. Live in a match via game/audio.ts's AudioSystem (§11). */
  masterVolume: number;
  /** 0..1. Menu background music only (ui/menuMusic.ts) — layered under
   *  masterVolume (final gain is musicVolume * masterVolume), not a
   *  replacement for it, so muting music alone never touches match SFX. */
  musicVolume: number;
  /**
   * Screen-centre aiming reticle (live ask "show a small cross-hair ... like
   * Valorant"), ui/hud.ts's `.fg-crosshair`. On by default — it existed
   * un-configurable before this setting; a toggle only ever HIDES what was
   * already shown, never surprises a returning player.
   */
  crosshairEnabled: boolean;
  /** Index into ui/crosshair-geometry.ts's CROSSHAIR_SIZE_PRESETS. No `live`
   *  hook needed (unlike camera/quality/input/audio below) — the game loop
   *  reads `settings.crosshairEnabled`/`crosshairSize` straight into HudState
   *  every frame already, so a settings-screen edit takes effect on the very
   *  next frame for free. */
  crosshairSize: number;
}

// Slider ranges — shared by the settings screen so UI and validation agree.
export const CAMERA_HEIGHT_RANGE = { min: 4, max: 45 } as const;
export const CAMERA_DISTANCE_RANGE = { min: 5, max: 60 } as const;
export const CAMERA_FOV_RANGE = { min: 30, max: 100 } as const;
/** Mouse/aim sensitivity band (rad/px). Deliberately WIDE so fast-flick players
 *  can raise it well above the comfortable ~0.0018 default; the hard safety
 *  floor/ceiling is the clamp in InputManager.setSensitivity. */
export const MOUSE_SENSITIVITY_RANGE = { min: 0.0007, max: 0.004 } as const;
export const AIM_SENSITIVITY_RANGE = { min: 0.0005, max: 0.004 } as const;
/** Camera smoothing in seconds (spring time). */
export const CAMERA_SMOOTHING_RANGE = { min: 0.08, max: 0.6 } as const;
/** Comfort default smoothing — inside the tuned 0.20–0.28 s band. */
export const DEFAULT_CAMERA_SMOOTHING = 0.24;
/** Ball Focus: 0 off / 1 soft / 2 full. */
export const BALL_FOCUS_CHOICES: readonly number[] = [0, 1, 2];
export const BALL_FOCUS_LABELS = ['Off', 'Soft', 'Full'] as const;
export const HALF_MINUTES_CHOICES: readonly number[] = [2, 3, 5];
export const QUALITY_CHOICES: readonly QualityChoice[] = ['auto', 'low', 'medium', 'high', 'ultra'];
export const DIFFICULTY_CHOICES: readonly Difficulty[] = ['easy', 'normal', 'hard'];
export const TIME_OF_DAY_CHOICES: readonly TimeOfDay[] = ['day', 'golden', 'overcast', 'night'];
export const WEATHER_CHOICES: readonly Weather[] = ['clear', 'rain'];

export const TIME_OF_DAY_LABELS: Record<TimeOfDay, string> = {
  day: 'Day',
  golden: 'Golden hour',
  overcast: 'Overcast',
  night: 'Night',
};
export const WEATHER_LABELS: Record<Weather, string> = {
  clear: 'Clear',
  rain: 'Rain (wet pitch)',
};

/**
 * The camera a fresh install plays in. (§7.1, §10, and a documented D0 override)
 *
 * ── Why this is NOT Broadcast any more (2026-07-25) ─────────────────────────
 *
 * D0 says "make BROADCAST the default". A later, more specific owner
 * instruction — *"lets use the cross to decide where the ball will go instead
 * of the arrow near his foot. and dont show the arrows near his foot"* — is
 * structurally incompatible with it, and the incompatibility is geometric, not
 * a matter of tuning:
 *
 *   - The reticle is pinned to the screen CENTRE (the owner's earlier ask, in
 *     his words *"cross-hair in the middle of the screen, a point where the
 *     ball will go"*), so the only heading it can honestly mark is the camera's
 *     own forward ray (game/crosshairAim.ts).
 *   - `updateBroadcast` (render/camera.ts's field-cam path) is a FIXED touchline
 *     rig — `camZ = smoothZ + distance`, look straight down −Z, no yaw term
 *     anywhere. Its forward ray is a CONSTANT −90°, whatever the mouse does.
 *   - So in Broadcast a centre reticle can only ever aim at the far touchline.
 *     `cameraAimsWithCrosshair` correctly refuses to let it aim there — and
 *     with the feet arrow deleted (the owner's third instruction) that left the
 *     DEFAULT camera with no aim indicator and no crosshair aiming at all.
 *
 * There are exactly three ways out, and only one of them keeps every owner
 * instruction: (a) give the broadcast rig a mouse orbit — but a rig you can
 * swing round to the far touchline is no longer "the EA-FC broadcast look", it
 * is TacticalFollow with different scalars, and D0's own reference screenshot
 * would then only be on screen until the first mouse movement; (b) let the
 * reticle leave the screen centre and aim by mouse position — contradicts the
 * "middle of the screen" instruction outright; (c) THIS — default to a camera
 * whose azimuth the player owns, and leave Broadcast on the shelf, byte-for-byte
 * unchanged, one camera-cycle press (C) or one settings dropdown away.
 *
 * TacticalFollow is the closest aimable mode to D0's geometry — it tracks the
 * SAME weighted centroid the broadcast cam tracks (camera.ts `followTarget`,
 * shared verbatim), elevated 1.25× and pulled back 1.15× from the same
 * TUNABLES.camera height/distance, looking down at the play. What it adds is
 * the one thing the crosshair needs: an azimuth the mouse owns (§10).
 *
 * ⚠ If this ever goes back to a fixed-azimuth rig, `defaultCameraAim.test.ts`
 * fails rather than the game: it pins "the default mode aims with the crosshair"
 * against the REAL CameraSystem.
 */
export const DEFAULT_CAMERA_MODE: CameraMode = CameraMode.TacticalFollow;

/**
 * Defaults mirror the shipped constants: the camera group starts at whatever
 * TUNABLES.camera booted with, so "never touched settings" and "no settings
 * file" are exactly the same game. (The MODE is the one deliberate exception —
 * see DEFAULT_CAMERA_MODE.)
 */
export function defaultSettings(): GameSettings {
  return {
    version: 3,
    playerName: '',
    camera: {
      mode: DEFAULT_CAMERA_MODE, // §10 Immersive Tactical — see DEFAULT_CAMERA_MODE
      height: TUNABLES.camera.height,
      distance: TUNABLES.camera.distance,
      fov: TUNABLES.camera.fov,
      mouseSensitivity: DEFAULT_MOUSE_SENSITIVITY,
      aimSensitivity: DEFAULT_AIM_SENSITIVITY,
      // Comfort default in the 0.20–0.28 s band — snappy but not twitchy.
      smoothing: DEFAULT_CAMERA_SMOOTHING,
      invertY: false,
      ballFocus: 0,
    },
    quality: 'auto',
    controls: defaultControlMap(),
    halfMinutes: 3,
    difficulty: 'normal',
    timeOfDay: 'day',
    weather: 'clear',
    masterVolume: 0.8,
    // Background music sits quieter than the 0.8 master default on purpose —
    // it is a mood bed behind menu navigation, not the main event; a player
    // who wants it louder (or off) has the slider. Lowered again 2026-08-21
    // (owner: "reduce music") from 0.35 — it was still reading as too present
    // under menu navigation.
    musicVolume: 0.18,
    crosshairEnabled: true,
    crosshairSize: DEFAULT_CROSSHAIR_SIZE_INDEX,
  };
}

function clampNum(v: unknown, min: number, max: number, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, v));
}

function isDifficulty(v: unknown): v is Difficulty {
  return v === 'easy' || v === 'normal' || v === 'hard';
}

function isQualityChoice(v: unknown): v is QualityChoice {
  return v === 'auto' || v === 'low' || v === 'medium' || v === 'high' || v === 'ultra';
}

/**
 * Parse an untrusted JSON string into a fully-valid GameSettings. Never throws;
 * every invalid/missing field independently falls back to its default.
 */
export function parseSettings(json: string | null | undefined): GameSettings {
  const out = defaultSettings();
  if (typeof json !== 'string' || json.length === 0) return out;

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return out;
  }
  if (typeof raw !== 'object' || raw === null) return out;
  const r = raw as Record<string, unknown>;

  if (typeof r['playerName'] === 'string') out.playerName = r['playerName'].slice(0, 16);

  const cam = r['camera'];
  if (typeof cam === 'object' && cam !== null) {
    const c = cam as Record<string, unknown>;
    const mode = c['mode'];
    if (
      typeof mode === 'number' &&
      Number.isInteger(mode) &&
      mode >= CameraMode.Broadcast &&
      mode <= CameraMode.TacticalFollow &&
      // Free is spectator/replay-only — nothing in a match drives it, so a
      // persisted Free would park the camera. Fall back to the default.
      mode !== CameraMode.Free
    ) {
      out.camera.mode = mode;
    }
    out.camera.height = clampNum(c['height'], CAMERA_HEIGHT_RANGE.min, CAMERA_HEIGHT_RANGE.max, out.camera.height);
    out.camera.distance = clampNum(c['distance'], CAMERA_DISTANCE_RANGE.min, CAMERA_DISTANCE_RANGE.max, out.camera.distance);
    out.camera.fov = clampNum(c['fov'], CAMERA_FOV_RANGE.min, CAMERA_FOV_RANGE.max, out.camera.fov);
    out.camera.mouseSensitivity = clampNum(
      c['mouseSensitivity'], MOUSE_SENSITIVITY_RANGE.min, MOUSE_SENSITIVITY_RANGE.max, out.camera.mouseSensitivity);
    out.camera.aimSensitivity = clampNum(
      c['aimSensitivity'], AIM_SENSITIVITY_RANGE.min, AIM_SENSITIVITY_RANGE.max, out.camera.aimSensitivity);
    out.camera.smoothing = clampNum(
      c['smoothing'], CAMERA_SMOOTHING_RANGE.min, CAMERA_SMOOTHING_RANGE.max, out.camera.smoothing);
    if (typeof c['invertY'] === 'boolean') out.camera.invertY = c['invertY'];
    if (BALL_FOCUS_CHOICES.includes(c['ballFocus'] as number)) out.camera.ballFocus = c['ballFocus'] as number;
  }

  if (isQualityChoice(r['quality'])) out.quality = r['quality'];
  out.controls = parseControlMap(r['controls']);
  if (HALF_MINUTES_CHOICES.includes(r['halfMinutes'] as number)) {
    out.halfMinutes = r['halfMinutes'] as number;
  }
  if (isDifficulty(r['difficulty'])) out.difficulty = r['difficulty'];
  if ((TIME_OF_DAY_CHOICES as readonly unknown[]).includes(r['timeOfDay'])) {
    out.timeOfDay = r['timeOfDay'] as TimeOfDay;
  }
  if ((WEATHER_CHOICES as readonly unknown[]).includes(r['weather'])) {
    out.weather = r['weather'] as Weather;
  }
  out.masterVolume = clampNum(r['masterVolume'], 0, 1, out.masterVolume);
  out.musicVolume = clampNum(r['musicVolume'], 0, 1, out.musicVolume);

  if (typeof r['crosshairEnabled'] === 'boolean') out.crosshairEnabled = r['crosshairEnabled'];
  if (CROSSHAIR_SIZE_CHOICES.includes(r['crosshairSize'] as number)) {
    out.crosshairSize = r['crosshairSize'] as number;
  }

  return out;
}

export function serializeSettings(s: GameSettings): string {
  return JSON.stringify(s);
}

// ---------------------------------------------------------------------------
// Storage (guarded — tests and SSR-ish contexts have no localStorage)
// ---------------------------------------------------------------------------

/**
 * v2 → v3: keep EVERYTHING the tester tuned, retire only the stale camera mode.
 *
 * A v2 blob that says Broadcast cannot be read as a choice — Broadcast WAS the
 * default, so the overwhelmingly likely reading is "never touched it", and
 * leaving it would hand the returning player exactly the camera that has no
 * working crosshair aim (see DEFAULT_CAMERA_MODE). Any OTHER persisted mode was
 * a deliberate selection out of a dropdown and is preserved untouched — that is
 * the whole reason this is a migration and not a key bump.
 *
 * Pure and exported so it is testable without localStorage, like every other
 * function in this file's "logic" half.
 */
export function migrateV2(s: GameSettings): GameSettings {
  if (s.camera.mode === CameraMode.Broadcast) s.camera.mode = DEFAULT_CAMERA_MODE;
  s.version = 3;
  return s;
}

export function loadSettings(): GameSettings {
  try {
    if (typeof localStorage === 'undefined') return defaultSettings();
    const current = localStorage.getItem(SETTINGS_KEY);
    if (current !== null) return parseSettings(current);
    // No v3 blob: a returning tester's v2 one still holds their controls and
    // mouse feel. Read it once, migrate, and let the next save write v3. (The v2
    // key is deliberately NOT deleted — a rollback to the previous build must
    // still find their settings where it left them.)
    const legacy = localStorage.getItem(SETTINGS_KEY_V2);
    if (legacy === null) return defaultSettings();
    return migrateV2(parseSettings(legacy));
  } catch {
    return defaultSettings();
  }
}

export function saveSettings(s: GameSettings): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(SETTINGS_KEY, serializeSettings(s));
  } catch {
    // Quota/private-mode failures must never take the game down.
  }
}

// ---------------------------------------------------------------------------
// Application
// ---------------------------------------------------------------------------

/**
 * Push the persisted camera rig into TUNABLES.camera — the object
 * render/camera.ts reads live each frame (§7.1 live preview). Mode is NOT
 * applied here (CameraSystem instances own their mode); callers pass
 * `settings.camera.mode` to `camera.setMode` when they construct one.
 */
export function applyCameraSettings(s: GameSettings): void {
  TUNABLES.camera.height = s.camera.height;
  TUNABLES.camera.distance = s.camera.distance;
  TUNABLES.camera.fov = s.camera.fov;
  TUNABLES.camera.smoothTime = s.camera.smoothing;
}

/**
 * Push the persisted mouse-feel into a live InputManager. Camera height/distance/
 * FOV/smoothing ride TUNABLES (read every frame); the sensitivities live on the
 * InputManager instance, so they must be applied to it explicitly — on match
 * start and on every settings-screen edit. Typed structurally so this module
 * stays decoupled from the InputManager class.
 */
export function applyInputFeel(
  input: { setSensitivity(mouse: number, aim: number): void },
  s: GameSettings,
): void {
  input.setSensitivity(s.camera.mouseSensitivity, s.camera.aimSensitivity);
}

/** The renderer-constructor quality override: undefined = let auto-detect run. */
export function qualityOverride(s: GameSettings): QualityTier | undefined {
  return s.quality === 'auto' ? undefined : s.quality;
}
